import React, { useState, useRef, useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { cognitoAuthService } from '../services/cognitoAuth';

interface User {
  name?: string;
  email?: string;
  subscriptionStatus?:
    | 'free'
    | 'trial'
    | 'active'
    | 'canceled'
    | 'past_due'
    | 'expired';
  trialEndDate?: string;
}

interface UserDropdownProps {
  user: User;
}

const UserDropdown: React.FC<UserDropdownProps> = ({ user }) => {
  const { t } = useTranslation('common');
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const history = useHistory();

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const handleSignOut = async () => {
    try {
      await cognitoAuthService.logout();
      // Clear any local storage
      localStorage.clear();
      sessionStorage.clear();
      // Force full page reload to reset all contexts
      window.location.href = '/';
    } catch (error) {
      console.error('Sign out failed:', error);
    }
  };

  const handleMyAccount = () => {
    setIsOpen(false);
    history.push('/tracks');
  };

  const handleManageSubscription = () => {
    setIsOpen(false);
    history.push('/subscription');
  };

  const getInitials = (name?: string) => {
    if (!name) return 'U';
    const names = name.split(' ');
    if (names.length >= 2) {
      return `${names[0][0]}${names[1][0]}`.toUpperCase();
    }
    return name.substring(0, 2).toUpperCase();
  };

  return (
    <div className='user-dropdown' ref={dropdownRef}>
      <button
        className='user-dropdown-trigger-mobile'
        onClick={() => setIsOpen(!isOpen)}
        aria-expanded={isOpen}
        aria-haspopup='true'
      >
        <svg
          width='24'
          height='24'
          viewBox='0 0 24 24'
          fill='none'
          stroke='currentColor'
          stroke-width='2'
          stroke-linecap='round'
          stroke-linejoin='round'
        >
          <line x1='3' y1='12' x2='21' y2='12'></line>
          <line x1='3' y1='6' x2='21' y2='6'></line>
          <line x1='3' y1='18' x2='21' y2='18'></line>
        </svg>
      </button>

      <button
        className='user-dropdown-trigger'
        onClick={() => setIsOpen(!isOpen)}
        aria-expanded={isOpen}
        aria-haspopup='true'
      >
        <div className='user-avatar'>
          {getInitials(user.name || user.email)}
        </div>
        <svg
          className={`dropdown-chevron ${isOpen ? 'open' : ''}`}
          width='20'
          height='20'
          viewBox='0 0 24 24'
          fill='none'
          stroke='currentColor'
          strokeWidth='2'
          strokeLinecap='round'
          strokeLinejoin='round'
        >
          <path d='M6 9l6 6 6-6' />
        </svg>
      </button>

      {isOpen && (
        <div className='user-dropdown-menu'>
          <div className='dropdown-header'>
            <div className='dropdown-user-info'>
              <div className='dropdown-user-name'>
                {user.name || user.email}
              </div>
              <div className='dropdown-user-email'>{user.email}</div>
            </div>
          </div>

          <div className='dropdown-divider'></div>

          <button className='dropdown-item' onClick={handleMyAccount}>
            <svg
              width='20'
              height='20'
              viewBox='0 0 24 24'
              fill='none'
              stroke='currentColor'
              strokeWidth='2'
              strokeLinecap='round'
              strokeLinejoin='round'
            >
              <path d='M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2'></path>
              <circle cx='12' cy='7' r='4'></circle>
            </svg>
            {t('nav.soundCatalog')}
          </button>

          <button className='dropdown-item' onClick={handleManageSubscription}>
            <svg
              width='20'
              height='20'
              viewBox='0 0 24 24'
              fill='none'
              stroke='currentColor'
              strokeWidth='2'
              strokeLinecap='round'
              strokeLinejoin='round'
            >
              <rect x='3' y='8' width='18' height='4' rx='1'></rect>
              <path d='M12 8v13'></path>
              <path d='M19 12v7a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2v-7'></path>
              <path d='M7.5 8a2.5 2.5 0 0 1 0-5A4.8 8 0 0 1 12 8a4.8 8 0 0 1 4.5-5 2.5 2.5 0 0 1 0 5'></path>
            </svg>
            {t('nav.manageSubscription')}
          </button>

          <div className='dropdown-divider'></div>

          <button className='dropdown-item logout' onClick={handleSignOut}>
            <svg
              width='20'
              height='20'
              viewBox='0 0 24 24'
              fill='none'
              stroke='currentColor'
              strokeWidth='2'
              strokeLinecap='round'
              strokeLinejoin='round'
            >
              <path d='M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4'></path>
              <polyline points='16 17 21 12 16 7'></polyline>
              <line x1='21' y1='12' x2='9' y2='12'></line>
            </svg>
            {t('nav.logout')}
          </button>
        </div>
      )}
    </div>
  );
};

export default UserDropdown;
